import axios from 'axios';
import { API_URL } from '../config/constants';
import { getTokenAuth, removeTokenAuth } from './authUtils';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';
export const AUTH_SESSION_SYNC_EVENT = 'auth:session-sync';

const AUTH_SESSION_SYNC_STORAGE_KEY = 'authSessionSyncEvent';
const AUTH_SESSION_SYNC_CHANNEL = 'krillou-auth-session';

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/register-owner',
  '/auth/register-tenant',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification-email'
];

let unauthorizedEventInFlight = false;
let fetchInterceptorInstalled = false;
let authBroadcastChannel = null;
let loginRedirectScheduled = false;

const getBroadcastChannel = () => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return null;
  }

  if (!authBroadcastChannel) {
    authBroadcastChannel = new window.BroadcastChannel(AUTH_SESSION_SYNC_CHANNEL);
  }

  return authBroadcastChannel;
};

const getRequestUrl = (input, baseURL) => {
  if (!input) {
    return '';
  }

  if (typeof input === 'string') {
    if (baseURL) {
      try {
        return new URL(input, baseURL).toString();
      } catch (error) {
        return input;
      }
    }

    return input;
  }

  if (typeof input.url === 'string') {
    return input.url;
  }

  return String(input);
};

const getRequestPath = (input, baseURL) => {
  const requestUrl = getRequestUrl(input, baseURL);

  try {
    return new URL(requestUrl, window.location.origin).pathname;
  } catch (error) {
    return requestUrl;
  }
};

const isManagedApiPath = (path) => {
  if (!path || typeof path !== 'string') {
    return false;
  }

  try {
    const apiPath = new URL(API_URL).pathname;
    return path.startsWith(apiPath) || path.startsWith('/api/');
  } catch (error) {
    return path.startsWith('/api/');
  }
};

const isPublicAuthPath = (path) => {
  return PUBLIC_AUTH_PATHS.some((publicPath) => path.includes(publicPath));
};

const hasStoredSession = () => {
  return Boolean(getTokenAuth() || localStorage.getItem('userData'));
};

const clearStoredSession = () => {
  removeTokenAuth();
  localStorage.removeItem('userData');
  localStorage.removeItem('userExplicitCurrencyChoice');
};

export const broadcastAuthSessionEvent = (type, details = {}) => {
  const payload = {
    type,
    details,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(AUTH_SESSION_SYNC_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Unable to write auth session sync event to localStorage', error);
  }

  const channel = getBroadcastChannel();
  if (channel) {
    channel.postMessage(payload);
  }
};

export const subscribeToAuthSessionEvents = (callback) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleStorage = (event) => {
    if (event.key !== AUTH_SESSION_SYNC_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      callback(JSON.parse(event.newValue));
    } catch (error) {
      console.warn('Unable to parse auth session sync storage event', error);
    }
  };

  window.addEventListener('storage', handleStorage);

  const channel = getBroadcastChannel();
  const handleBroadcast = (event) => {
    if (event?.data) {
      callback(event.data);
    }
  };

  if (channel) {
    channel.addEventListener('message', handleBroadcast);
  }

  return () => {
    window.removeEventListener('storage', handleStorage);
    if (channel) {
      channel.removeEventListener('message', handleBroadcast);
    }
  };
};

export const notifyUnauthorizedSession = (details = {}) => {
  clearStoredSession();
  broadcastAuthSessionEvent('unauthorized', details);

  if (unauthorizedEventInFlight) {
    return;
  }

  unauthorizedEventInFlight = true;
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT, { detail: details }));

  // Safety net: force redirect to login if React routing doesn't handle it
  scheduleLoginRedirect();

  window.setTimeout(() => {
    unauthorizedEventInFlight = false;
  }, 1500);
};

export const notifyManualLogout = (details = {}) => {
  clearStoredSession();
  broadcastAuthSessionEvent('logout', details);
};

/**
 * Safety net: if React declarative routing (ProtectedRoute) doesn't redirect
 * to /login within 500ms, force a hard navigation.
 */
export const scheduleLoginRedirect = () => {
  if (typeof window === 'undefined') return;
  if (loginRedirectScheduled) return;
  loginRedirectScheduled = true;

  setTimeout(() => {
    loginRedirectScheduled = false;
    const path = window.location.pathname;
    const alreadyOnAuth = path === '/login' || PUBLIC_AUTH_PATHS.some((p) => path.includes(p));
    if (!alreadyOnAuth) {
      window.location.replace('/login');
    }
  }, 500);
};

export const handleAxiosUnauthorizedError = (error) => {
  if (error?.response?.status !== 401) {
    return null;
  }

  const requestPath = getRequestPath(error?.config?.url, error?.config?.baseURL);
  const hasAuthorizationHeader = Boolean(
    error?.config?.headers?.Authorization || error?.config?.headers?.authorization
  );

  // Never intercept public auth endpoints (login, register, etc.)
  if (isPublicAuthPath(requestPath)) {
    return null;
  }

  // If the request carried our Bearer token and got 401 → token is expired.
  // This is the primary check – it doesn't depend on path matching.
  if (hasAuthorizationHeader) {
    notifyUnauthorizedSession({
      source: 'axios',
      path: requestPath,
      status: 401
    });
    return new Promise(() => {});
  }

  // Fallback: no auth header, but a stored session exists on a managed API path
  if (isManagedApiPath(requestPath) && hasStoredSession()) {
    notifyUnauthorizedSession({
      source: 'axios',
      path: requestPath,
      status: 401
    });
    return new Promise(() => {});
  }

  return null;
};

export const attachUnauthorizedInterceptor = (axiosInstance) => {
  if (!axiosInstance || axiosInstance.__hasUnauthorizedInterceptor) {
    return axiosInstance;
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const unauthorizedPromise = handleAxiosUnauthorizedError(error);
      if (unauthorizedPromise) {
        return unauthorizedPromise;
      }

      return Promise.reject(error);
    }
  );

  axiosInstance.__hasUnauthorizedInterceptor = true;
  return axiosInstance;
};

export const installFetchUnauthorizedInterceptor = () => {
  if (fetchInterceptorInstalled || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    const requestPath = getRequestPath(input);
    const hasAuthorizationHeader = Boolean(
      init?.headers instanceof Headers
        ? init.headers.get('Authorization') || init.headers.get('authorization')
        : init?.headers?.Authorization || init?.headers?.authorization
    );

    if (
      response.status === 401 &&
      isManagedApiPath(requestPath) &&
      !isPublicAuthPath(requestPath) &&
      (hasAuthorizationHeader || hasStoredSession())
    ) {
      notifyUnauthorizedSession({
        source: 'fetch',
        path: requestPath,
        status: 401
      });

      return new Promise(() => {});
    }

    return response;
  };

  fetchInterceptorInstalled = true;
};

attachUnauthorizedInterceptor(axios);